import { ProposalDataType } from 'types/proposals'
import { StudioSystemType } from 'types/studio/items'
import { SystemDataType } from 'types/systems'

// SystemDataType (proposal/API shape) and StudioSystemType (in-editor shape) overlap on
// the fields used in customer proposal/checkout flows (uuid, payment_options, userData).
// The studio shape extends with class methods (e.g. moduleGrids()) that are NOT available
// on the proposal/API shape; callers that rely on studio-only methods must NOT use this
// resolver in customer-facing flows where studio may be hidden.
export type ResolvedSystem = StudioSystemType | SystemDataType

/** DB system id from studio ``userData`` or proposal ``SystemDataType.id``. */
export const getResolvedSystemUserDataId = (
  system: ResolvedSystem | undefined | null
): number | undefined => {
  if (!system) {
    return undefined
  }
  if ('userData' in system && system.userData?.id != null) {
    return system.userData.id
  }
  if ('id' in system && typeof system.id === 'number') {
    return system.id
  }
  return undefined
}

/**
 * Resolve a system from `proposalData` (API/Redux shape) by uuid.
 *
 * Looks up `proposalData.systems[]` first, then falls back to `proposalData.selectedSystem`
 * if its uuid matches. Returns `undefined` when nothing matches.
 */
export const resolveSystemFromProposal = (
  systemUuid: string | null | undefined,
  proposalData: ProposalDataType | undefined
): SystemDataType | undefined => {
  if (!systemUuid || !proposalData) {
    return undefined
  }

  const fromSystems = proposalData.systems?.find((s) => s?.uuid === systemUuid)

  if (fromSystems) {
    return fromSystems
  }

  if (proposalData.selectedSystem?.uuid === systemUuid) {
    return proposalData.selectedSystem
  }

  return undefined
}

/**
 * Resolve a system by uuid, preferring the in-editor studio system when available and
 * falling back to `proposalData` (Redux/API shape) when studio is hidden.
 *
 * When `use_prerendered_system_image` is on, studio runs in
 * 'hidden' mode and `window.editor.getSystems()` is empty. Customer proposal/checkout
 * code paths should always pass `proposalData` so the resolver can fall back to it.
 */
export const resolveSystem = (
  systemUuid: string | null | undefined,
  proposalData?: ProposalDataType | undefined
): ResolvedSystem | undefined => {
  if (!systemUuid) {
    return undefined
  }

  const fromEditor = window.editor?.getSystems?.()?.find((s) => s.uuid === systemUuid)

  if (fromEditor) {
    return fromEditor
  }

  return resolveSystemFromProposal(systemUuid, proposalData)
}
